import { useEffect, useRef } from "react";

/**
 * Swipe-down-to-close for the dashboard mobile bottom drawers (drilldown drawer + MobileBottomSheet).
 *
 * Hand-rolled on raw touch events — the webapp has no gesture library, and this is the only swipe
 * gesture in it. The gesture logic lives in `createSwipeDownMachine`, a DOM-free state machine, so
 * it can be unit-tested under jest's node environment (no jsdom here); `useSwipeDownToClose` is the
 * thin DOM adapter.
 *
 * Touch events, not pointer events: AG Grid's drag service calls stopPropagation() on POINTER
 * events (see useCloseBlockPopoversOnOutsideTap.ts), but its touch handling is passive, so touch
 * events reach us even from inside a drilldown table. Listeners still register in the CAPTURE
 * phase as belt-and-braces against any injected block content that stops touch propagation.
 *
 * Listeners attach to the sheet element itself, not the document. Touch events implicitly capture
 * to the touchstart target's ancestor chain, so the drag keeps firing on the sheet even when the
 * finger leaves it — and fixed panels portaled to the iframe body as siblings of the sheet (the
 * mobile DateRangeSlicer panel) can never trigger a dismiss.
 */

/** Movement below this is a tap: no transform, no preventDefault, buttons keep working. */
const SLOP_PX = 8;
/** Release dismisses when dragged past this fraction of the sheet's height... */
const DISMISS_DISTANCE_RATIO = 0.3;
/** ...or when flicked at least this far... */
const FLICK_MIN_DISTANCE_PX = 24;
/** ...at at least this downward speed (px/ms). */
const FLICK_MIN_VELOCITY = 0.5;
/**
 * A still finger fires no touchmove, so the last samples keep reporting the speed of whatever
 * movement happened last. Past this gap between the final sample and the release, the gesture is
 * treated as having stopped (velocity 0) — otherwise "drag fast, hold, lift" reads as a flick and
 * dismisses a sheet the user had visibly settled.
 */
const STALE_VELOCITY_MS = 100;
/** Matches the drawers' `duration-300` enter transitions. */
const EXIT_TRANSITION_MS = 300;
/** Fires if transitionend never does (e.g. settle released at exactly 0px → no transition). */
const TRANSITION_FALLBACK_MS = 400;

export interface SwipeDownMachineConfig {
  /** True when a scroller under the finger is scrolled down — the gesture then scrolls, not dismisses. */
  isScrollBlocked: () => boolean;
  /** Sheet height at release time, for the distance threshold. */
  getSheetHeight: () => number;
  /**
   * The sheet's current visual downward offset, read once when a drag commits.
   *
   * Non-zero only when the gesture interrupts an in-flight animation — chiefly the 300ms enter
   * transition, since the hook is live from mount. Committing a drag disables transitions, which
   * cancels that transition and snaps the sheet to its target position, so the offset is added to
   * every subsequent translate to keep the sheet under the finger where it was grabbed instead of
   * jumping to fully-open first.
   */
  getBaseOffset: () => number;
  /** Follow the finger: translate the sheet down by `px` (≥ 0) with transitions disabled. */
  setTransform: (px: number) => void;
  /** Released short/slow: animate back to rest. Machine stays non-interactive until animationFinished(). */
  settle: () => void;
  /** Released far/fast: animate off-screen, then dismiss. Machine stays non-interactive until animationFinished(). */
  dismiss: () => void;
}

export interface SwipeDownMachine {
  /** One call per placed finger; `totalTouches` is the active-touch count including this one. */
  touchStart: (id: number, x: number, y: number, t: number, totalTouches: number) => void;
  /** One call per moved finger. Returns whether the touchmove event must be preventDefault-ed. */
  touchMove: (id: number, x: number, y: number, t: number) => boolean;
  /** One call per lifted finger; `remainingTouches` is the count still down after this lift. */
  touchEnd: (id: number, t: number, remainingTouches: number) => void;
  touchCancel: () => void;
  /**
   * Reports every touch identifier currently down. If the finger being tracked is not among them,
   * its event stream was lost (its touchstart target was detached mid-gesture, so subsequent
   * touchmove/touchend dispatch at the orphaned node and never reach the sheet) and the gesture is
   * abandoned. Without this the machine stays stuck mid-drag and a later tap that reuses the same
   * identifier would be released against the stale drag distance.
   */
  syncActiveTouches: (ids: number[]) => void;
  /** The adapter reports the settle/dismiss animation finished; new gestures may begin. */
  animationFinished: () => void;
  reset: () => void;
}

/**
 * Gesture state machine: idle → pending → (dragging | rejected) → (settling/dismissing) → idle.
 *
 * The pending→dragging/rejected decision happens ONCE, on the first move past slop, and rejection
 * is sticky until every finger lifts — a gesture that starts as a horizontal table pan or as a
 * scroll of a scrolled-down list can never mid-flight turn into a dismiss.
 */
export function createSwipeDownMachine(config: SwipeDownMachineConfig): SwipeDownMachine {
  type Phase = "idle" | "pending" | "dragging" | "rejected" | "animating";
  let phase: Phase = "idle";
  let trackedId = 0;
  let x0 = 0;
  let y0 = 0;
  let t0 = 0;
  // Last two samples of the tracked finger, for release velocity.
  let prev: { y: number; t: number } | null = null;
  let last: { y: number; t: number } | null = null;
  // Where the sheet already sat when this drag committed; 0 unless it interrupted an animation.
  let baseOffset = 0;

  const touchStart: SwipeDownMachine["touchStart"] = (id, x, y, t, totalTouches) => {
    // A rejected gesture only returns to idle when its last finger lifts, but fingers that landed
    // outside the sheet never deliver a touchend here. `totalTouches` counts every touch in the
    // document and includes this new one, so 1 proves the rejected gesture is fully over —
    // otherwise its stale rejection would silently swallow this next swipe.
    if (phase === "rejected" && totalTouches === 1) {
      phase = "idle";
    }
    if (phase === "idle") {
      if (totalTouches === 1) {
        phase = "pending";
        trackedId = id;
        x0 = x;
        y0 = y;
        t0 = t;
        prev = null;
        last = null;
        baseOffset = 0;
      } else {
        // Two fingers landed in one event — a pinch, not a swipe.
        phase = "rejected";
      }
      return;
    }
    if (phase === "pending") {
      // Second finger before the gesture committed: treat as a pinch (charts zoom).
      phase = "rejected";
    }
    // dragging: extra fingers are ignored, the original finger keeps the drag.
    // rejected/animating: ignored.
  };

  const touchMove: SwipeDownMachine["touchMove"] = (id, x, y, t) => {
    if (id !== trackedId) {
      return false;
    }
    if (phase === "pending") {
      const dx = x - x0;
      const dy = y - y0;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < SLOP_PX) {
        return false;
      }
      if (dy > 0 && Math.abs(dy) > Math.abs(dx) && !config.isScrollBlocked()) {
        phase = "dragging";
        last = { y, t };
        // Read before the first setTransform, which disables transitions and therefore destroys
        // the in-flight position this is capturing.
        baseOffset = config.getBaseOffset();
        config.setTransform(baseOffset + dy);
        return true;
      }
      phase = "rejected";
      return false;
    }
    if (phase === "dragging") {
      prev = last;
      last = { y, t };
      // Clamp at 0: dragging back above the start pins the sheet where it was grabbed, no over-lift.
      config.setTransform(baseOffset + Math.max(0, y - y0));
      return true;
    }
    return false;
  };

  const touchEnd: SwipeDownMachine["touchEnd"] = (id, t, remainingTouches) => {
    if (phase === "pending" && id === trackedId) {
      phase = "idle";
      return;
    }
    if (phase === "dragging" && id === trackedId) {
      const dy = Math.max(0, (last?.y ?? y0) - y0);
      // With a single move sample, velocity falls back to gesture start; dt is clamped so a
      // same-millisecond start/end can't divide by zero. A release long after the last sample
      // means the finger had come to rest, so there is no flick to honour.
      const from = prev ?? { y: y0, t: t0 };
      const to = last ?? { y: y0, t: t0 };
      const velocity =
        t - to.t > STALE_VELOCITY_MS ? 0 : (to.y - from.y) / Math.max(1, to.t - from.t);
      const sheetHeight = config.getSheetHeight();
      // sheetHeight > 0 guards a mis-measured (collapsed) sheet from making every drag dismiss.
      const shouldDismiss =
        (sheetHeight > 0 && dy >= DISMISS_DISTANCE_RATIO * sheetHeight) ||
        (dy >= FLICK_MIN_DISTANCE_PX && velocity >= FLICK_MIN_VELOCITY);
      phase = "animating";
      if (shouldDismiss) {
        config.dismiss();
      } else {
        config.settle();
      }
      return;
    }
    if (phase === "rejected" && remainingTouches === 0) {
      phase = "idle";
    }
  };

  const touchCancel: SwipeDownMachine["touchCancel"] = () => {
    // The system stole the touch (edge gesture, notification). Never dismiss on a cancel.
    if (phase === "dragging") {
      phase = "animating";
      config.settle();
      return;
    }
    if (phase === "pending" || phase === "rejected") {
      phase = "idle";
    }
  };

  const syncActiveTouches: SwipeDownMachine["syncActiveTouches"] = ids => {
    if ((phase !== "pending" && phase !== "dragging") || ids.includes(trackedId)) {
      return;
    }
    // Same handling as a system-stolen touch: never dismiss on a stream we stopped seeing.
    touchCancel();
  };

  return {
    touchStart,
    touchMove,
    touchEnd,
    touchCancel,
    syncActiveTouches,
    animationFinished: () => {
      phase = "idle";
    },
    reset: () => {
      phase = "idle";
    },
  };
}

/**
 * Walks `start` up to `sheetEl` (inclusive) looking for a scrolled-down vertical scroller. If one
 * exists, a downward swipe there must scroll it back up, not dismiss the sheet. Covers the sheet
 * body scroller, the drilldown drawer's scroller, and AG Grid's .ag-body-viewport generically.
 *
 * `start` is an event target, duck-typed on nodeType rather than `instanceof Node` — this module is
 * bundled in the parent document while the drawers' elements belong to the dashboard iframe's
 * realm, where `instanceof` is always false (see useCloseBlockPopoversOnOutsideTap.ts). Computed
 * style comes from the element's own realm for the same reason.
 */
export function findBlockingScrollable(start: unknown, sheetEl: HTMLElement): HTMLElement | null {
  const startNode = start && typeof (start as Node).nodeType === "number" ? (start as Node) : null;
  let node: Node | null = startNode;
  while (node) {
    if (node.nodeType === 1 /* ELEMENT_NODE */) {
      const el = node as HTMLElement;
      // `> 0`, not `!== 0`: iOS rubber-banding reports negative scrollTop at the top edge.
      if (el.scrollTop > 0 && el.scrollHeight > el.clientHeight) {
        const overflowY = el.ownerDocument.defaultView?.getComputedStyle(el).overflowY;
        if (overflowY === "auto" || overflowY === "scroll") {
          return el;
        }
      }
      if (el === sheetEl) {
        return null;
      }
    }
    node = node.parentNode;
  }
  return null;
}

/** Resolves one component of the `translate` shorthand, which may be a percentage of the box. */
const resolveTranslateComponent = (raw: string, boxSize: number): number =>
  raw.endsWith("%") ? ((parseFloat(raw) || 0) / 100) * boxSize : parseFloat(raw) || 0;

/**
 * The element's current visual downward offset in px, mid-animation included.
 *
 * Reads BOTH `translate` and `transform`, because they are separate properties that compose: the
 * drawers' enter animations come from Tailwind's `translate-y-full`/`translate-y-0` (the `translate`
 * longhand, where the value is a PERCENTAGE of the element's own height), while this hook drives the
 * drag through `transform`. Reading only one would miss half the offset.
 */
export function currentVisualOffsetY(el: HTMLElement): number {
  const style = el.ownerDocument.defaultView?.getComputedStyle(el);
  if (!style) {
    return 0;
  }
  let offset = 0;
  if (style.translate && style.translate !== "none") {
    // "0px" (x only, y implicitly 0), "0px 100%", or "0px 100% 0px" for the 3-arg form.
    const parts = style.translate.trim().split(/\s+/);
    if (parts.length > 1) {
      offset += resolveTranslateComponent(parts[1], el.offsetHeight);
    }
  }
  if (style.transform && style.transform !== "none") {
    // Computed transforms are always matrices: matrix(a,b,c,d,tx,ty) / matrix3d(...16 values).
    const matrix2d = style.transform.match(/^matrix\(([^)]+)\)$/);
    if (matrix2d) {
      offset += parseFloat(matrix2d[1].split(",")[5]) || 0;
    } else {
      const matrix3d = style.transform.match(/^matrix3d\(([^)]+)\)$/);
      if (matrix3d) {
        offset += parseFloat(matrix3d[1].split(",")[13]) || 0;
      }
    }
  }
  return offset;
}

/**
 * Lets the user swipe the sheet element down to dismiss it. Mobile-only by way of `enabled` —
 * callers gate it on their own useIsMobile. The hook owns the whole exit animation and calls
 * `onDismiss` only after the sheet has fully slid out, because neither drawer animates its own
 * close (MobileBottomSheet unmounts instantly; the drilldown drawer's `invisible` hides instantly).
 *
 * After a dismiss the inline transform is deliberately NOT cleared until the effect cleanup runs
 * (enabled flips false / unmount): clearing it synchronously, before React commits the close,
 * would flash the sheet back to translate-y-0 for a frame.
 */
export function useSwipeDownToClose({
  sheetRef,
  enabled,
  onDismiss,
}: {
  sheetRef: React.RefObject<HTMLElement>;
  enabled: boolean;
  onDismiss: () => void;
}): void {
  // Ref'd so identity churn in the caller's closure doesn't tear down mid-gesture listeners.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    const sheetEl = sheetRef.current;
    if (!enabled || !sheetEl) {
      return;
    }
    const view = sheetEl.ownerDocument.defaultView ?? window;

    // Where the current gesture's first finger landed; scroll gating walks up from here.
    let gestureTarget: unknown = null;
    let fallbackTimer: number | null = null;
    let transitionListener: ((e: TransitionEvent) => void) | null = null;

    const cancelAnimationWatch = () => {
      if (fallbackTimer != null) {
        view.clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      if (transitionListener) {
        sheetEl.removeEventListener("transitionend", transitionListener);
        transitionListener = null;
      }
    };

    // Transition is set explicitly rather than relying on each drawer's classes, so the animation
    // is identical for both drawers and immune to their class churn while closing.
    const animateTo = (transform: string, onDone: () => void) => {
      cancelAnimationWatch();
      sheetEl.style.transition = `transform ${EXIT_TRANSITION_MS}ms ease-out`;
      sheetEl.style.transform = transform;
      const finish = () => {
        cancelAnimationWatch();
        onDone();
        machine.animationFinished();
      };
      transitionListener = e => {
        if (e.target === sheetEl && e.propertyName === "transform") {
          finish();
        }
      };
      sheetEl.addEventListener("transitionend", transitionListener);
      fallbackTimer = view.setTimeout(finish, TRANSITION_FALLBACK_MS);
    };

    const machine = createSwipeDownMachine({
      isScrollBlocked: () => findBlockingScrollable(gestureTarget, sheetEl) != null,
      getSheetHeight: () => sheetEl.getBoundingClientRect().height,
      // Clamped at 0 so a sheet caught mid-exit (or any upward offset) can't drag from above rest.
      getBaseOffset: () => Math.max(0, currentVisualOffsetY(sheetEl)),
      setTransform: px => {
        sheetEl.style.transition = "none";
        sheetEl.style.transform = `translateY(${px}px)`;
      },
      settle: () =>
        animateTo("", () => {
          sheetEl.style.transition = "";
          sheetEl.style.transform = "";
        }),
      // translateY(100%) is relative to the element's own height, so it slides exactly off-screen
      // for both the fixed-height filters sheet and the content-height drilldown drawer.
      dismiss: () => animateTo("translateY(100%)", () => onDismissRef.current()),
    });

    const touchIds = (list: TouchList): number[] => {
      const ids: number[] = [];
      for (let i = 0; i < list.length; i++) {
        ids.push(list[i].identifier);
      }
      return ids;
    };

    const handleTouchStart = (e: TouchEvent) => {
      // Abandon a gesture whose finger is no longer down: its touchstart target was detached
      // mid-drag, so its remaining events went to the orphaned node instead of here.
      machine.syncActiveTouches(touchIds(e.touches));
      if (e.touches.length === 1) {
        gestureTarget = e.target;
      }
      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        machine.touchStart(
          touch.identifier,
          touch.clientX,
          touch.clientY,
          e.timeStamp,
          e.touches.length
        );
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      let prevent = false;
      for (let i = 0; i < e.changedTouches.length; i++) {
        const touch = e.changedTouches[i];
        if (machine.touchMove(touch.identifier, touch.clientX, touch.clientY, e.timeStamp)) {
          prevent = true;
        }
      }
      if (prevent && e.cancelable) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        machine.touchEnd(e.changedTouches[i].identifier, e.timeStamp, e.touches.length);
      }
    };

    const handleTouchCancel = () => machine.touchCancel();

    // touchmove is the one non-passive listener: preventDefault-ing a qualifying move is what
    // stops the inner scroller / rubber-band from eating the drag.
    sheetEl.addEventListener("touchstart", handleTouchStart, { capture: true, passive: true });
    sheetEl.addEventListener("touchmove", handleTouchMove, { capture: true, passive: false });
    sheetEl.addEventListener("touchend", handleTouchEnd, { capture: true, passive: true });
    sheetEl.addEventListener("touchcancel", handleTouchCancel, { capture: true, passive: true });

    return () => {
      sheetEl.removeEventListener("touchstart", handleTouchStart, true);
      sheetEl.removeEventListener("touchmove", handleTouchMove, true);
      sheetEl.removeEventListener("touchend", handleTouchEnd, true);
      sheetEl.removeEventListener("touchcancel", handleTouchCancel, true);
      cancelAnimationWatch();
      sheetEl.style.transition = "";
      sheetEl.style.transform = "";
      machine.reset();
    };
  }, [enabled, sheetRef]);
}
